'use client';

import React from 'react';
import Link from 'next/link';
import { ChevronRight, Home } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';

// ─────────────────────────────────────────────
// PAGE HEADER
// Unified enterprise page header with breadcrumbs
// ─────────────────────────────────────────────

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  badge,
  actions,
  className = '',
}: PageHeaderProps) {
  return (
    <div data-component-id="src/components/ui/EnterpriseComponents.tsx:36:4" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="36" data-component-end-line="71" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`mb-6 ${className}`}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav data-component-id="src/components/ui/EnterpriseComponents.tsx:39:8" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="39" data-component-end-line="55" data-component-file="EnterpriseComponents.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22flex%20items-center%20gap-1%20mb-3%22%7D" className="flex items-center gap-1 mb-3" aria-label="Breadcrumb">
          <Link data-component-id="src/components/ui/EnterpriseComponents.tsx:40:10" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="40" data-component-end-line="42" data-component-file="EnterpriseComponents.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%2C%22className%22%3A%22breadcrumb-item%20hover%3Atext-foreground%22%7D" href="/" className="breadcrumb-item hover:text-foreground" aria-label="Home">
            <Home data-component-id="src/components/ui/EnterpriseComponents.tsx:41:12" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="41" data-component-end-line="41" data-component-file="EnterpriseComponents.tsx" data-component-name="Home" data-component-content="%7B%22elementName%22%3A%22Home%22%7D" size={11} />
          </Link>
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={`bc-${i}`}>
              <ChevronRight data-component-id="src/components/ui/EnterpriseComponents.tsx:45:14" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="45" data-component-end-line="45" data-component-file="EnterpriseComponents.tsx" data-component-name="ChevronRight" data-component-content="%7B%22elementName%22%3A%22ChevronRight%22%2C%22className%22%3A%22text-muted-foreground%2F40%22%7D" size={11} className="text-muted-foreground/40" />
              {crumb.href && i < breadcrumbs.length - 1 ? (
                <Link data-component-id="src/components/ui/EnterpriseComponents.tsx:47:16" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="47" data-component-end-line="49" data-component-file="EnterpriseComponents.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22breadcrumb-item%22%7D" href={crumb.href} className="breadcrumb-item">
                  {crumb.label}
                </Link>
              ) : (
                <span data-component-id="src/components/ui/EnterpriseComponents.tsx:51:16" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="51" data-component-end-line="51" data-component-file="EnterpriseComponents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22breadcrumb-item-active%20text-xs%22%7D" className="breadcrumb-item-active text-xs">{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Title row */}
      <div data-component-id="src/components/ui/EnterpriseComponents.tsx:59:6" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="59" data-component-end-line="70" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20sm%3Aitems-start%20sm%3Ajustify-between%20gap-3%22%7D" className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div data-component-id="src/components/ui/EnterpriseComponents.tsx:60:8" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="60" data-component-end-line="68" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-3%22%7D" className="flex items-start gap-3">
          <div data-component-id="src/components/ui/EnterpriseComponents.tsx:61:10" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="61" data-component-end-line="67" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <div data-component-id="src/components/ui/EnterpriseComponents.tsx:62:12" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="62" data-component-end-line="65" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2.5%20flex-wrap%22%7D" className="flex items-center gap-2.5 flex-wrap">
              <h1 data-component-id="src/components/ui/EnterpriseComponents.tsx:63:14" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="63" data-component-end-line="63" data-component-file="EnterpriseComponents.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22page-title%22%7D" className="page-title">{title}</h1>
              {badge && <div data-component-id="src/components/ui/EnterpriseComponents.tsx:64:24" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="64" data-component-end-line="64" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">{badge}</div>}
            </div>
            {subtitle && <p data-component-id="src/components/ui/EnterpriseComponents.tsx:66:25" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="66" data-component-end-line="66" data-component-file="EnterpriseComponents.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20mt-1%22%7D" className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
        </div>
        {actions && <div data-component-id="src/components/ui/EnterpriseComponents.tsx:69:20" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="69" data-component-end-line="69" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20shrink-0%22%7D" className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SECTION CARD
// Unified enterprise section card
// ─────────────────────────────────────────────

interface SectionCardProps {
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  noPadding?: boolean;
  elevated?: boolean;
}

export function SectionCard({
  title,
  subtitle,
  icon,
  badge,
  actions,
  children,
  className = '',
  noPadding = false,
  elevated = false,
}: SectionCardProps) {
  return (
    <div data-component-id="src/components/ui/EnterpriseComponents.tsx:104:4" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="104" data-component-end-line="119" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${elevated ? 'card-enterprise-elevated' : 'card-enterprise'} ${className}`}>
      {(title || actions) && (
        <div data-component-id="src/components/ui/EnterpriseComponents.tsx:106:8" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="106" data-component-end-line="116" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20px-4%20py-3%20border-b%20border-border%22%7D" className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div data-component-id="src/components/ui/EnterpriseComponents.tsx:107:10" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="107" data-component-end-line="114" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20min-w-0%22%7D" className="flex items-center gap-2 min-w-0">
            {icon && <div data-component-id="src/components/ui/EnterpriseComponents.tsx:108:21" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="108" data-component-end-line="108" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-muted-foreground%20shrink-0%22%7D" className="text-muted-foreground shrink-0">{icon}</div>}
            <div data-component-id="src/components/ui/EnterpriseComponents.tsx:109:12" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="109" data-component-end-line="112" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%22%7D" className="min-w-0">
              {title && <h3 data-component-id="src/components/ui/EnterpriseComponents.tsx:110:24" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="110" data-component-end-line="110" data-component-file="EnterpriseComponents.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22card-title%20truncate%22%7D" className="card-title truncate">{title}</h3>}
              {subtitle && <p data-component-id="src/components/ui/EnterpriseComponents.tsx:111:27" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="111" data-component-end-line="111" data-component-file="EnterpriseComponents.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22caption%20mt-0.5%22%7D" className="caption mt-0.5">{subtitle}</p>}
            </div>
            {badge && <div data-component-id="src/components/ui/EnterpriseComponents.tsx:113:22" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="113" data-component-end-line="113" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22shrink-0%20ml-1%22%7D" className="shrink-0 ml-1">{badge}</div>}
          </div>
          {actions && <div data-component-id="src/components/ui/EnterpriseComponents.tsx:115:22" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="115" data-component-end-line="115" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20shrink-0%20ml-3%22%7D" className="flex items-center gap-2 shrink-0 ml-3">{actions}</div>}
        </div>
      )}
      <div data-component-id="src/components/ui/EnterpriseComponents.tsx:118:6" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="118" data-component-end-line="118" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={noPadding ? '' : 'p-4'}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────
// METRIC GRID
// Consistent metric card grid
// ─────────────────────────────────────────────

interface MetricGridProps {
  children: React.ReactNode;
  cols?: 2 | 3 | 4 | 5;
  className?: string;
}

export function MetricGrid({ children, cols = 4, className = '' }: MetricGridProps) {
  const colClass =
    cols === 2
      ? 'grid-cols-1 sm:grid-cols-2'
      : cols === 3
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
        : cols === 5
          ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'
          : 'grid-cols-2 lg:grid-cols-4';
  return <div data-component-id="src/components/ui/EnterpriseComponents.tsx:143:9" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="143" data-component-end-line="143" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`grid ${colClass} gap-4 ${className}`}>{children}</div>;
}

// ─────────────────────────────────────────────
// ENTERPRISE DATA TABLE
// ─────────────────────────────────────────────

interface TableColumn<T> {
  key: string;
  header: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render: (row: T, index: number) => React.ReactNode;
}

interface EnterpriseTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  emptyState?: React.ReactNode;
  className?: string;
  stickyHeader?: boolean;
}

export function EnterpriseTable<T>({
  columns,
  data,
  emptyState,
  className = '',
  stickyHeader = false,
}: EnterpriseTableProps<T>) {
  return (
    <div data-component-id="src/components/ui/EnterpriseComponents.tsx:174:4" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="174" data-component-end-line="226" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`overflow-x-auto ${className}`}>
      <table data-component-id="src/components/ui/EnterpriseComponents.tsx:175:6" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="175" data-component-end-line="225" data-component-file="EnterpriseComponents.tsx" data-component-name="table" data-component-content="%7B%22elementName%22%3A%22table%22%2C%22className%22%3A%22table-enterprise%22%7D" className="table-enterprise">
        <thead data-component-id="src/components/ui/EnterpriseComponents.tsx:176:8" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="176" data-component-end-line="194" data-component-file="EnterpriseComponents.tsx" data-component-name="thead" data-component-content="%7B%22elementName%22%3A%22thead%22%7D" className={stickyHeader ? 'sticky top-0 z-10' : ''}>
          <tr data-component-id="src/components/ui/EnterpriseComponents.tsx:177:10" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="177" data-component-end-line="193" data-component-file="EnterpriseComponents.tsx" data-component-name="tr" data-component-content="%7B%22elementName%22%3A%22tr%22%7D">
            {columns.map((col) => (
              <th data-component-id="src/components/ui/EnterpriseComponents.tsx:179:14" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="179" data-component-end-line="191" data-component-file="EnterpriseComponents.tsx" data-component-name="th" data-component-content="%7B%22elementName%22%3A%22th%22%7D"
                key={`th-${col.key}`}
                style={{ width: col.width }}
                className={
                  col.align === 'right'
                    ? 'text-right'
                    : col.align === 'center'
                      ? 'text-center'
                      : 'text-left'
                }
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody data-component-id="src/components/ui/EnterpriseComponents.tsx:195:8" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="195" data-component-end-line="224" data-component-file="EnterpriseComponents.tsx" data-component-name="tbody" data-component-content="%7B%22elementName%22%3A%22tbody%22%7D">
          {data.length === 0 ? (
            <tr data-component-id="src/components/ui/EnterpriseComponents.tsx:197:12" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="197" data-component-end-line="203" data-component-file="EnterpriseComponents.tsx" data-component-name="tr" data-component-content="%7B%22elementName%22%3A%22tr%22%7D">
              <td data-component-id="src/components/ui/EnterpriseComponents.tsx:198:14" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="198" data-component-end-line="202" data-component-file="EnterpriseComponents.tsx" data-component-name="td" data-component-content="%7B%22elementName%22%3A%22td%22%2C%22className%22%3A%22text-center%20py-12%22%7D" colSpan={columns.length} className="text-center py-12">
                {emptyState ?? (
                  <span data-component-id="src/components/ui/EnterpriseComponents.tsx:200:18" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="200" data-component-end-line="200" data-component-file="EnterpriseComponents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%22%2C%22textContent%22%3A%22No%20data%20available%22%7D" className="text-xs text-muted-foreground">No data available</span>
                )}
              </td>
            </tr>
          ) : (
            data.map((row, i) => (
              <tr data-component-id="src/components/ui/EnterpriseComponents.tsx:206:14" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="206" data-component-end-line="221" data-component-file="EnterpriseComponents.tsx" data-component-name="tr" data-component-content="%7B%22elementName%22%3A%22tr%22%2C%22className%22%3A%22table-row-hover%22%7D" key={`tr-${i}`} className="table-row-hover">
                {columns.map((col) => (
                  <td data-component-id="src/components/ui/EnterpriseComponents.tsx:208:18" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="208" data-component-end-line="219" data-component-file="EnterpriseComponents.tsx" data-component-name="td" data-component-content="%7B%22elementName%22%3A%22td%22%7D"
                    key={`td-${i}-${col.key}`}
                    className={
                      col.align === 'right'
                        ? 'text-right'
                        : col.align === 'center'
                          ? 'text-center'
                          : ''
                    }
                  >
                    {col.render(row, i)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────
// ENTERPRISE TABS
// ─────────────────────────────────────────────

interface TabItem {
  id: string;
  label: string;
  icon?: React.ElementType;
  count?: number;
}

interface EnterpriseTabsProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (id: string) => void;
  className?: string;
  scrollable?: boolean;
}

export function EnterpriseTabs({
  tabs,
  activeTab,
  onTabChange,
  className = '',
  scrollable = true,
}: EnterpriseTabsProps) {
  return (
    <div data-component-id="src/components/ui/EnterpriseComponents.tsx:257:4" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="257" data-component-end-line="288" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`${scrollable ? 'overflow-x-auto' : ''} ${className}`}
      role="tablist"
      aria-label="Module tabs"
    >
      <div data-component-id="src/components/ui/EnterpriseComponents.tsx:262:6" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="262" data-component-end-line="287" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-1%20min-w-max%20p-1%20bg-muted%2F40%20border%20border-border%20rounded-lg%22%7D" className="flex gap-1 min-w-max p-1 bg-muted/40 border border-border rounded-lg">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button data-component-id="src/components/ui/EnterpriseComponents.tsx:267:12" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="267" data-component-end-line="284" data-component-file="EnterpriseComponents.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={`tab-${tab.id}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              onClick={() => onTabChange(tab.id)}
              className={`tab-enterprise ${isActive ? 'tab-enterprise-active' : ''}`}
            >
              {Icon && <Icon data-component-id="src/components/ui/EnterpriseComponents.tsx:275:23" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="275" data-component-end-line="275" data-component-file="EnterpriseComponents.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" size={13} />}
              <span data-component-id="src/components/ui/EnterpriseComponents.tsx:276:14" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="276" data-component-end-line="276" data-component-file="EnterpriseComponents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{tab.label}</span>
              {tab.count !== undefined && (
                <span data-component-id="src/components/ui/EnterpriseComponents.tsx:278:16" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="278" data-component-end-line="282" data-component-file="EnterpriseComponents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                  className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SOURCE LABEL
// ─────────────────────────────────────────────

interface SourceLabelProps {
  source: string;
  className?: string;
}

export function SourceLabel({ source, className = '' }: SourceLabelProps) {
  return (
    <span data-component-id="src/components/ui/EnterpriseComponents.tsx:303:4" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="303" data-component-end-line="308" data-component-file="EnterpriseComponents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
      className={`inline-flex items-center gap-1 text-2xs font-mono text-muted-foreground/70 bg-muted/40 border border-border/50 rounded px-1.5 py-0.5 ${className}`}
    >
      <span data-component-id="src/components/ui/EnterpriseComponents.tsx:306:6" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="306" data-component-end-line="306" data-component-file="EnterpriseComponents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-muted-foreground%2F40%22%2C%22textContent%22%3A%22Source%3A%22%7D" className="text-muted-foreground/40">Source:</span>
      {source}
    </span>
  );
}

// ─────────────────────────────────────────────
// PHASE BADGE
// ─────────────────────────────────────────────

interface PhaseBadgeProps {
  phase: string;
  className?: string;
}

export function PhaseBadge({ phase, className = '' }: PhaseBadgeProps) {
  return (
    <span data-component-id="src/components/ui/EnterpriseComponents.tsx:323:4" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="323" data-component-end-line="327" data-component-file="EnterpriseComponents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Phase%22%7D"
      className={`inline-flex items-center gap-1 text-2xs font-mono font-semibold px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 ${className}`}
    >
      Phase {phase}
    </span>
  );
}

// ─────────────────────────────────────────────
// MODULE ID BADGE
// ─────────────────────────────────────────────

interface ModuleIdBadgeProps {
  id: string;
  className?: string;
}

export function ModuleIdBadge({ id, className = '' }: ModuleIdBadgeProps) {
  return (
    <span data-component-id="src/components/ui/EnterpriseComponents.tsx:342:4" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="342" data-component-end-line="346" data-component-file="EnterpriseComponents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
      className={`inline-flex items-center text-2xs font-mono font-semibold px-2 py-0.5 rounded bg-muted border border-border text-muted-foreground ${className}`}
    >
      {id}
    </span>
  );
}

// ─────────────────────────────────────────────
// DIVIDER
// ─────────────────────────────────────────────

export function Divider({ className = '' }: { className?: string }) {
  return <div data-component-id="src/components/ui/EnterpriseComponents.tsx:355:9" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="355" data-component-end-line="355" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`divider-enterprise ${className}`} role="separator" />;
}

// ─────────────────────────────────────────────
// PROGRESS BAR
// ─────────────────────────────────────────────

interface ProgressBarProps {
  value: number;
  max?: number;
  label?: string;
  showPercent?: boolean;
  className?: string;
}

export function ProgressBar({
  value,
  max = 100,
  label,
  showPercent = true,
  className = '',
}: ProgressBarProps) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div data-component-id="src/components/ui/EnterpriseComponents.tsx:379:4" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="379" data-component-end-line="395" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`space-y-1 ${className}`}>
      {(label || showPercent) && (
        <div data-component-id="src/components/ui/EnterpriseComponents.tsx:381:8" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="381" data-component-end-line="384" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
          {label && <span data-component-id="src/components/ui/EnterpriseComponents.tsx:382:20" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="382" data-component-end-line="382" data-component-file="EnterpriseComponents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%22%7D" className="text-xs text-muted-foreground">{label}</span>}
          {showPercent && <span data-component-id="src/components/ui/EnterpriseComponents.tsx:383:26" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="383" data-component-end-line="383" data-component-file="EnterpriseComponents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-mono%20text-foreground%22%2C%22textContent%22%3A%22%25%22%7D" className="text-xs font-mono text-foreground">{pct}%</span>}
        </div>
      )}
      <div data-component-id="src/components/ui/EnterpriseComponents.tsx:386:6" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="386" data-component-end-line="394" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22progress-enterprise%22%7D"
        className="progress-enterprise"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div data-component-id="src/components/ui/EnterpriseComponents.tsx:393:8" data-component-path="src/components/ui/EnterpriseComponents.tsx" data-component-line="393" data-component-end-line="393" data-component-file="EnterpriseComponents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22progress-enterprise-fill%22%7D" className="progress-enterprise-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
